export async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    return await loadImageFromUrl(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function fetchImage(url: string): Promise<HTMLImageElement> {
  return loadImageFromUrl(url);
}

export async function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  try {
    return await loadImageFromUrl(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function createPreviewImageCopy(
  source: HTMLImageElement,
  maxSize: number
): Promise<{ image: HTMLImageElement; src: string }> {
  const sourceWidth = Math.max(1, source.naturalWidth || source.width || 1);
  const sourceHeight = Math.max(1, source.naturalHeight || source.height || 1);
  const maxSide = Math.max(sourceWidth, sourceHeight);
  const scale = Math.min(1, maxSize / maxSide);
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = createCanvas(targetWidth, targetHeight);
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('2D context is not available for preview copy canvas');
  }

  ctx.drawImage(source, 0, 0, targetWidth, targetHeight);
  const blob = await canvasToBlob(canvas, 'image/png');
  const src = URL.createObjectURL(blob);

  try {
    const image = await loadImageFromUrl(src);
    return { image, src };
  } catch (error) {
    URL.revokeObjectURL(src);
    throw error;
  }
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  type = 'image/png',
  quality?: number
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas export failed'));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
}

export async function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await canvasToBlob(canvas, 'image/png');
  const buffer = await blob.arrayBuffer();
  return new Uint8Array(buffer);
}

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return trackCanvas(canvas);
}

export async function loadVideoFromFile(file: File): Promise<HTMLVideoElement> {
  const url = URL.createObjectURL(file);
  const video = await loadVideoFromUrl(url);
  setManagedMediaUrl(video, url);
  return video;
}

export async function loadVideoFromUrl(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    if (shouldUseAnonymousCors(url)) {
      video.crossOrigin = 'anonymous';
    }

    const cleanup = (): void => {
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('error', onError);
    };

    const onLoadedData = (): void => {
      cleanup();
      resolve(video);
    };

    const onError = (): void => {
      cleanup();
      reject(new Error('Unable to load video'));
    };

    video.addEventListener('loadeddata', onLoadedData, { once: true });
    video.addEventListener('error', onError, { once: true });
    video.src = url;
    video.load();
  });
}

export interface SeekVideoOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function seekVideo(
  video: HTMLVideoElement,
  nextTime: number,
  options: SeekVideoOptions = {}
): Promise<void> {
  throwIfRenderAborted(options.signal);
  const duration = Number.isFinite(video.duration) ? video.duration : null;
  const maxTime = duration && duration > 0 ? Math.max(0, duration - 1 / 120) : Math.max(0, nextTime);
  const clampedTime = Math.max(0, Math.min(nextTime, maxTime));

  if (Math.abs(video.currentTime - clampedTime) < 1 / 1000) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const timeoutId = window.setTimeout(
      () => finishReject(new Error('Video seek stopped responding.')),
      options.timeoutMs ?? DEFAULT_RENDER_OPERATION_TIMEOUT_MS
    );
    const cleanup = (): void => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      options.signal?.removeEventListener('abort', onAbort);
    };

    const finishResolve = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      resolve();
    };

    const finishReject = (error: Error): void => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      reject(error);
    };

    const onSeeked = (): void => {
      finishResolve();
    };

    const onError = (): void => {
      finishReject(new Error('Unable to seek video'));
    };

    const onAbort = (): void => {
      finishReject(getRenderAbortError(options.signal));
    };

    video.addEventListener('seeked', onSeeked, { once: true });
    video.addEventListener('error', onError, { once: true });
    options.signal?.addEventListener('abort', onAbort, { once: true });
    try {
      video.currentTime = clampedTime;
    } catch (error) {
      finishReject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

export function revokeManagedMediaUrl(node: HTMLMediaElement | HTMLImageElement): void {
  const managedUrl = getManagedMediaUrl(node);
  if (managedUrl && managedUrl.startsWith('blob:')) {
    URL.revokeObjectURL(managedUrl);
  }
  clearManagedMediaUrl(node);
}

export function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    if (shouldUseAnonymousCors(url)) {
      image.crossOrigin = 'anonymous';
    }
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load image'));
    image.src = url;
  });
}

function shouldUseAnonymousCors(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function setManagedMediaUrl(node: HTMLMediaElement | HTMLImageElement, url: string): void {
  (node as HTMLMediaElement & { __managedObjectUrl?: string }).__managedObjectUrl = url;
}

function getManagedMediaUrl(node: HTMLMediaElement | HTMLImageElement): string {
  return ((node as HTMLMediaElement & { __managedObjectUrl?: string }).__managedObjectUrl || '').trim();
}

function clearManagedMediaUrl(node: HTMLMediaElement | HTMLImageElement): void {
  delete (node as HTMLMediaElement & { __managedObjectUrl?: string }).__managedObjectUrl;
}
import {
  DEFAULT_RENDER_OPERATION_TIMEOUT_MS,
  getRenderAbortError,
  throwIfRenderAborted
} from './RenderTask';
import { trackCanvas } from './canvasMemory';
